import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "brand_manager", label: "Brand Manager" },
  { value: "marketing_executive", label: "Marketing Executive" },
  { value: "viewer", label: "Viewer / Management" },
];

const STATUSES = ["active", "inactive"];

const ROLE_TONES = {
  admin: "border-rose-200 bg-rose-50 text-rose-700",
  brand_manager: "border-blue-200 bg-blue-50 text-blue-700",
  marketing_executive: "border-emerald-200 bg-emerald-50 text-emerald-700",
  viewer: "border-slate-200 bg-slate-50 text-slate-700",
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(null); // user object when opening
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer", assigned_brand_ids: [] });
  const [pwForm, setPwForm] = useState("");

  const load = () => {
    api.get("/auth/users").then((r) => setUsers(r.data || []));
    api.get("/brands").then((r) => setBrands(r.data || []));
  };
  useEffect(load, []);

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/auth/users/${editing.id}`, {
          name: form.name, role: form.role, assigned_brand_ids: form.assigned_brand_ids,
        });
        toast.success("User updated");
      } else {
        await api.post("/auth/register", {
          name: form.name, email: form.email, password: form.password,
          role: form.role,
          assigned_brand_ids: form.assigned_brand_ids || [],
        });
        toast.success("User invited");
      }
      setOpen(false); setEditing(null);
      setForm({ name: "", email: "", password: "", role: "viewer", assigned_brand_ids: [] });
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const toggleStatus = async (u) => {
    try {
      const next = (u.status === "active") ? "inactive" : "active";
      await api.put(`/auth/users/${u.id}`, { status: next });
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const onEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, assigned_brand_ids: u.assigned_brand_ids || [] });
    setOpen(true);
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/auth/users/${u.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(extractError(e)); }
  };

  const resetPw = async () => {
    if (pwForm.length < 6) return toast.error("Password must be 6+ chars");
    try {
      await api.post(`/auth/users/${pwOpen.id}/reset-password`, { new_password: pwForm });
      toast.success("Password reset");
      setPwOpen(null); setPwForm("");
    } catch (e) { toast.error(extractError(e)); }
  };

  const toggleBrand = (id) => setForm((f) => {
    const has = (f.assigned_brand_ids || []).includes(id);
    return { ...f, assigned_brand_ids: has ? f.assigned_brand_ids.filter((x) => x !== id) : [...(f.assigned_brand_ids || []), id] };
  });

  if (me?.role !== "admin") {
    return <div className="text-sm text-slate-500">Admin access required.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Workspace</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Users</h1>
          <p className="mt-1 text-sm text-slate-500">{users.length} accounts · invite team members and manage roles + brand access.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({ name: "", email: "", password: "", role: "viewer", assigned_brand_ids: [] }); } }}>
          <DialogTrigger asChild>
            <Button data-testid="user-add-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg">
              <Plus className="h-4 w-4 mr-1" /> Invite user
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Edit user" : "Invite user"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <F label="Name" required>
                <Input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </F>
              {!editing && (
                <>
                  <F label="Email" required>
                    <Input data-testid="user-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </F>
                  <F label="Initial password" required>
                    <Input data-testid="user-form-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  </F>
                </>
              )}
              <F label="Role">
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              {editing && (
                <F label="Assigned brands">
                  <div className="flex flex-wrap gap-1.5">
                    {brands.map((b) => {
                      const has = (form.assigned_brand_ids || []).includes(b.id);
                      return (
                        <button key={b.id} type="button" onClick={() => toggleBrand(b.id)}
                          data-testid={`user-form-brand-${b.brand_name}`}
                          className={`rounded-full border px-3 py-1 text-xs font-semibold ${has ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                          {b.brand_name}
                        </button>
                      );
                    })}
                    {!brands.length && <span className="text-xs text-slate-400">No brands</span>}
                  </div>
                </F>
              )}
              {!editing && form.role !== "admin" && (
                <F label={<span className="inline-flex items-center gap-1.5">Assigned brands <span className="text-[10px] font-normal normal-case text-slate-400">(optional · empty = all brands)</span></span>}>
                  <div className="flex flex-wrap gap-1.5">
                    {brands.map((b) => {
                      const has = (form.assigned_brand_ids || []).includes(b.id);
                      return (
                        <button key={b.id} type="button" onClick={() => toggleBrand(b.id)}
                          data-testid={`user-form-brand-${b.brand_name}`}
                          className={`rounded-full border px-3 py-1 text-xs font-semibold ${has ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                          {b.brand_name}
                        </button>
                      );
                    })}
                    {!brands.length && <span className="text-xs text-slate-400">No brands</span>}
                  </div>
                </F>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="user-form-save" onClick={save} className="bg-slate-900 text-white hover:bg-slate-800">{editing ? "Save" : "Invite"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left">User</th>
              <th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Assigned brands</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{u.name}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${ROLE_TONES[u.role]}`}>
                    {(ROLES.find((r) => r.value === u.role) || {}).label || u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleStatus(u)} data-testid={`user-status-${u.id}`}
                    className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${u.status === "active" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                    {u.status}
                  </button>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {u.assigned_brand_ids?.length
                    ? u.assigned_brand_ids.map((id) => brands.find((b) => b.id === id)?.brand_name).filter(Boolean).join(", ")
                    : <span className="text-slate-400">all</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => onEdit(u)} data-testid={`user-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setPwOpen(u)} data-testid={`user-reset-pw-${u.id}`}><KeyRound className="h-3.5 w-3.5" /></Button>
                    {u.id !== me.id && (
                      <Button variant="ghost" size="sm" onClick={() => onDelete(u)} className="text-rose-600 hover:text-rose-700" data-testid={`user-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!pwOpen} onOpenChange={(v) => { if (!v) { setPwOpen(null); setPwForm(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password for {pwOpen?.email}</DialogTitle></DialogHeader>
          <div className="py-2">
            <F label="New password (6+ chars)" required>
              <Input data-testid="reset-pw-input" type="password" value={pwForm} onChange={(e) => setPwForm(e.target.value)} />
            </F>
          </div>
          <DialogFooter><Button data-testid="reset-pw-save" onClick={resetPw} className="bg-slate-900 text-white hover:bg-slate-800">Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">
        {label}{required ? <span className="text-rose-500"> *</span> : null}
      </Label>
      {children}
    </div>
  );
}
